import { ContractError, HttpError, createApiClient, parseOrThrow } from '../../api';
import { languageChoiceOf } from '../../i18n/languages';
import { reportUnreadableDetails } from '../reporting';
import { SIGNED_OUT } from '../types';
import type { DetailsState, ServerSession, SessionUser, ViewingAs } from '../types';

import { personDetailsSchema, sessionAnswerSchema, sessionIdentitySchema } from './schemas';
import type { PersonDetailsDto, SessionIdentityDto, ViewingAsDto } from './schemas';

/** The cookie-authenticated "who am I" read — the one read of the signed-in person the whole app shares. */
export const SESSION_ENDPOINT = '/v2/auth/session';

const SESSION_TIMEOUT_MS = 8_000;

// Asking whether there is a session must never end one: a 401 here is the answer
// "signed out", not a session lost mid-use, so nothing is cleared or redirected.
const { request } = createApiClient({ onSessionLost: () => undefined });

const userOf = (identity: SessionIdentityDto): SessionUser => ({
  id: identity._id,
  name: identity.name ?? '',
  email: identity.email ?? '',
  isStaff: identity.isStaff === true,
});

const viewingAsOf = (viewer: ViewingAsDto | null | undefined): ViewingAs | null =>
  viewer === null || viewer === undefined
    ? null
    : { id: viewer.id, name: viewer.name ?? '', email: viewer.email ?? '', isStaff: viewer.isStaff === true };

const detailsOf = (details: PersonDetailsDto): DetailsState => ({
  status: 'read',
  value: {
    number: details.number ?? null,
    language: languageChoiceOf(details.language),
    phoneVerified: details.isPhoneNumberVerified === true,
    emailVerified: details.isEmailAddressVerified === true,
    passwordSet: details.passwordSet ?? undefined,
    pictureUrl: details.profilePic ?? null,
    hasDashboardChoice: Boolean(details.defaultPage),
  },
});

// A drifted detail is reported and held as unreadable: the screens that show it fail, sign-in does not.
const detailsStateOf = (user: Readonly<Record<string, unknown>>): DetailsState => {
  const parsed = personDetailsSchema.safeParse(user);
  if (parsed.success) return detailsOf(parsed.data);
  const error = new ContractError('Unexpected person details response shape', parsed.error.issues);
  reportUnreadableDetails(error);
  return { status: 'unreadable', error };
};

const isSignedOut = (error: unknown): boolean => error instanceof HttpError && error.status === 401;

/** Who is signed in on this browser, from the server — the httpOnly cookie is the session; this describes it. */
export const fetchServerSession = async (signal?: AbortSignal): Promise<ServerSession> => {
  let raw: unknown;
  try {
    raw = await request('get', SESSION_ENDPOINT, { signal, timeoutMs: SESSION_TIMEOUT_MS });
  } catch (error) {
    if (isSignedOut(error)) return SIGNED_OUT;
    throw error;
  }
  const { user, viewingAs, landingPage } = parseOrThrow(sessionAnswerSchema, raw, 'session');
  if (user === null) return SIGNED_OUT;
  return {
    status: 'signedIn',
    user: userOf(parseOrThrow(sessionIdentitySchema, user, 'session')),
    details: detailsStateOf(user),
    viewingAs: viewingAsOf(viewingAs),
    landingPage: landingPage ?? null,
  };
};
